import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Lock, Unlock, FileDown, ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, History, Eye } from 'lucide-react';
import { toast } from 'sonner';

const FRECUENCIAS = [
  { value: 'semanal', label: 'Semanal', desc: 'Cierra cada 7 dias' },
  { value: 'quincenal', label: 'Quincenal', desc: 'Dia 1-15 y 16-fin de mes' },
  { value: 'mensual', label: 'Mensual', desc: 'Del 1 al ultimo dia del mes' },
];

const DIAS_SEMANA = [
  { value: 0, label: 'Lunes' }, { value: 1, label: 'Martes' },
  { value: 2, label: 'Miercoles' }, { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' }, { value: 5, label: 'Sabado' },
  { value: 6, label: 'Domingo' },
];

export default function CarwashCierres() {
  const [periodoActual, setPeriodoActual] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [cerrarDialog, setCerrarDialog] = useState(false);
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [detalleDialog, setDetalleDialog] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [pa, h] = await Promise.all([
        api.get('/carwash/cierres-periodo/periodo-actual'),
        api.get('/carwash/cierres-periodo'),
      ]);
      setPeriodoActual(pa.data);
      setHistorial(h.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarFrecuencia = async (frecuencia) => {
    try {
      await api.put('/configuracion', { frecuencia_contable: frecuencia });
      toast.success(`Frecuencia cambiada a ${frecuencia}`);
      cargar();
    } catch { toast.error('Error al actualizar'); }
  };

  const cambiarDiaCierre = async (dia) => {
    try {
      await api.put('/configuracion', { dia_cierre_semana: parseInt(dia) });
      toast.success('Dia de cierre actualizado');
      cargar();
    } catch { toast.error('Error al actualizar'); }
  };

  const cerrarPeriodo = async () => {
    setGuardando(true);
    try {
      await api.post('/carwash/cierres-periodo', { notas });
      toast.success('Periodo cerrado correctamente');
      setCerrarDialog(false);
      setNotas('');
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cerrar');
    } finally { setGuardando(false); }
  };

  const reabrirPeriodo = async (cierre) => {
    if (!window.confirm(`Reabrir el periodo ${cierre.etiqueta}? Podras editar pagos y gastos de esas fechas.`)) return;
    try {
      await api.delete(`/carwash/cierres-periodo/${cierre.id}`);
      toast.success('Periodo reabierto');
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const descargarPDF = (cierre) => {
    const tk = localStorage.getItem('token') || '';
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/carwash/cierres-periodo/${cierre.id}/pdf?token=${tk}`, '_blank');
  };

  if (cargando) {
    return <div className="space-y-4 animate-pulse">
      <div className="h-10 w-64 bg-slate-800 rounded" />
      <div className="h-48 bg-slate-800 rounded" />
    </div>;
  }

  const pa = periodoActual;
  const pct = pa ? Math.round((pa.dias_transcurridos / pa.dias_total) * 100) : 0;
  const utilidadColor = pa?.totales?.utilidad >= 0 ? 'text-emerald-400' : 'text-red-400';
  const comparativaUtilidad = historial[0]?.comparativa?.utilidad_pct;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-cierres-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Calendar className="w-8 h-8 text-cyan-400" /> Cierres de Periodo
        </h1>
      </div>

      {/* Configuración rápida */}
      <Card className="bg-slate-900/40 border-slate-800" data-testid="config-frecuencia-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-base text-slate-300 flex items-center gap-2">
            <History className="w-4 h-4 text-cyan-400" /> Configuracion del Periodo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Frecuencia</label>
              <Select value={pa?.frecuencia || 'semanal'} onValueChange={cambiarFrecuencia}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="frecuencia-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {FRECUENCIAS.map(f => (
                    <SelectItem key={f.value} value={f.value} className="text-slate-300">{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                {FRECUENCIAS.find(f => f.value === pa?.frecuencia)?.desc}
              </p>
            </div>
            {pa?.frecuencia === 'semanal' && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Dia de cierre</label>
                <Select value={String(pa?.dia_cierre ?? 6)} onValueChange={cambiarDiaCierre}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="dia-cierre-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {DIAS_SEMANA.map(d => (
                      <SelectItem key={d.value} value={String(d.value)} className="text-slate-300">{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-end">
              <p className="text-xs text-slate-500">
                Puedes cambiar la frecuencia en cualquier momento. Los cierres ya realizados se conservan.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Periodo Actual */}
      {pa && (
        <Card className="bg-gradient-to-br from-slate-900/60 to-slate-900/30 border-cyan-900/40" data-testid="periodo-actual-card">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold mb-1">Periodo en curso</p>
                <h2 className="text-2xl font-bold text-slate-50 font-barlow" data-testid="periodo-etiqueta">{pa.etiqueta}</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Del {pa.inicio} al {pa.fin} · {pa.dias_transcurridos} de {pa.dias_total} dias transcurridos
                  {pa.dias_restantes > 0 ? ` · ${pa.dias_restantes} dias restantes` : ' · Listo para cerrar'}
                </p>
              </div>
              {pa.dias_restantes === 0 ? (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-700 px-3 py-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1.5 inline" /> Es momento de cerrar
                </Badge>
              ) : (
                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-800 px-3 py-1.5">En curso</Badge>
              )}
            </div>

            {/* Progreso */}
            <div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all duration-700"
                  style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">{pct}% del periodo</p>
            </div>

            {/* Totales */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-950/50 border border-slate-800 p-3 rounded-sm" data-testid="totales-ingresos">
                <p className="text-[10px] uppercase text-slate-500 mb-1">Ingresos</p>
                <p className="text-lg font-bold text-emerald-400 font-mono">{formatoMXN(pa.totales.ingresos)}</p>
                <p className="text-[10px] text-slate-600">{pa.totales.turnos_count} servicios</p>
              </div>
              <div className="bg-slate-950/50 border border-slate-800 p-3 rounded-sm" data-testid="totales-gastos">
                <p className="text-[10px] uppercase text-slate-500 mb-1">Gastos</p>
                <p className="text-lg font-bold text-red-400 font-mono">{formatoMXN(pa.totales.gastos)}</p>
                <p className="text-[10px] text-slate-600">{pa.totales.gastos_count} registros</p>
              </div>
              <div className="bg-slate-950/50 border border-slate-800 p-3 rounded-sm" data-testid="totales-utilidad">
                <p className="text-[10px] uppercase text-slate-500 mb-1">Utilidad Neta</p>
                <p className={`text-lg font-bold font-mono ${utilidadColor}`}>{formatoMXN(pa.totales.utilidad)}</p>
                <p className="text-[10px] text-slate-600">{pa.totales.ingresos > 0 ? `${Math.round(pa.totales.utilidad / pa.totales.ingresos * 100)}% margen` : '—'}</p>
              </div>
              <div className="bg-slate-950/50 border border-slate-800 p-3 rounded-sm">
                <p className="text-[10px] uppercase text-slate-500 mb-1">Descuentos</p>
                <p className="text-lg font-bold text-amber-400 font-mono">{formatoMXN(pa.totales.descuentos)}</p>
                <p className="text-[10px] text-slate-600">Aplicados</p>
              </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-800">
              <div className="text-xs text-slate-500">
                Cerrar el periodo guardara este reporte de forma permanente y bloqueara la edicion de pagos/gastos del rango.
              </div>
              {/* Desglose de Nomina del Periodo */}
              {(pa.totales?.desglose_empleados || []).length > 0 && (
                <div className="mt-3 border border-slate-800 rounded-sm overflow-hidden" data-testid="desglose-nomina">
                  <div className="bg-slate-950/60 px-3 py-2 flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wider text-amber-500 font-bold">Desglose de Nómina del Periodo</p>
                    <p className="text-xs text-slate-400">
                      Neto a pagar: <span className="text-emerald-400 font-mono font-bold">{formatoMXN(pa.totales.total_nomina_neta || 0)}</span>
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-900/60 text-slate-500 uppercase tracking-wider text-[10px]">
                          <th className="px-2 py-1.5 text-left">Empleado</th>
                          <th className="px-2 py-1.5 text-left">Tipo pago</th>
                          <th className="px-2 py-1.5 text-center">Carros</th>
                          <th className="px-2 py-1.5 text-right">Servicios facturados</th>
                          <th className="px-2 py-1.5 text-right">Sueldo/Comisión</th>
                          <th className="px-2 py-1.5 text-center">Faltas</th>
                          <th className="px-2 py-1.5 text-right">Descuentos</th>
                          <th className="px-2 py-1.5 text-right font-bold">NETO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pa.totales.desglose_empleados.map(e => (
                          <tr key={e.empleado_id} className="border-t border-slate-800/40" data-testid={`nomina-row-${e.empleado_id}`}>
                            <td className="px-2 py-1.5 text-slate-200 font-medium">{e.nombre}</td>
                            <td className="px-2 py-1.5 text-slate-400 capitalize">
                              {e.tipo_pago === 'sueldo' && `Sueldo ${e.frecuencia_pago}`}
                              {e.tipo_pago === 'comision' && `Comisión ${e.porcentaje_comision}%`}
                              {e.tipo_pago === 'mixto' && `Mixto ${e.porcentaje_comision}%`}
                              {e.monto_por_carro > 0 && ` + ${formatoMXN(e.monto_por_carro)}/carro`}
                            </td>
                            <td className="px-2 py-1.5 text-center text-cyan-400 font-mono">{e.turnos}</td>
                            <td className="px-2 py-1.5 text-right text-slate-400 font-mono">{formatoMXN(e.servicios_facturados)}</td>
                            <td className="px-2 py-1.5 text-right text-slate-200 font-mono">
                              {e.tipo_pago === 'sueldo' ? formatoMXN(e.sueldo_periodo) :
                               e.tipo_pago === 'comision' ? formatoMXN(e.comision_total) :
                               formatoMXN(e.bruto)}
                            </td>
                            <td className="px-2 py-1.5 text-center text-slate-400">{e.faltas_count || '—'}</td>
                            <td className="px-2 py-1.5 text-right text-red-400 font-mono">
                              {e.descuento_faltas > 0 ? `-${formatoMXN(e.descuento_faltas)}` : '$0'}
                            </td>
                            <td className="px-2 py-1.5 text-right text-emerald-400 font-mono font-bold">{formatoMXN(e.neto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <Button onClick={() => setCerrarDialog(true)}
                disabled={pa.cerrado}
                className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm"
                data-testid="cerrar-periodo-btn">
                <Lock className="w-4 h-4 mr-2" /> Cerrar Periodo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historial */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-slate-200">
            Historial de Cierres ({historial.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {historial.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500">Aun no has cerrado ningun periodo</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Periodo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Ingresos</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Gastos</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Utilidad</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">vs Anterior</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historial.map(c => {
                  const t = c.totales || {};
                  const cp = c.comparativa;
                  return (
                    <TableRow key={c.id} className="border-slate-800/50" data-testid={`cierre-row-${c.id}`}>
                      <TableCell className="text-sm text-slate-200 font-medium">{c.etiqueta}</TableCell>
                      <TableCell>
                        <Badge className="bg-slate-800/60 text-slate-400 border-slate-700 text-[10px] capitalize">
                          {c.frecuencia}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-emerald-400 font-mono">{formatoMXN(t.ingresos)}</TableCell>
                      <TableCell className="text-right text-sm text-red-400 font-mono">{formatoMXN(t.gastos)}</TableCell>
                      <TableCell className={`text-right text-sm font-mono font-bold ${t.utilidad >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                        {formatoMXN(t.utilidad)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {cp?.utilidad_pct !== null && cp?.utilidad_pct !== undefined ? (
                          <span className={`inline-flex items-center gap-1 font-mono ${cp.utilidad_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {cp.utilidad_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {cp.utilidad_pct >= 0 ? '+' : ''}{cp.utilidad_pct}%
                          </span>
                        ) : <span className="text-slate-600">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setDetalleDialog(c)}
                            className="text-slate-400 hover:text-cyan-400 h-8 w-8" data-testid={`ver-cierre-${c.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => descargarPDF(c)}
                            className="text-slate-400 hover:text-amber-400 h-8 w-8" data-testid={`pdf-cierre-${c.id}`}>
                            <FileDown className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => reabrirPeriodo(c)}
                            className="text-slate-400 hover:text-red-400 h-8 w-8" data-testid={`reabrir-cierre-${c.id}`}>
                            <Unlock className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Cerrar Periodo */}
      <Dialog open={cerrarDialog} onOpenChange={setCerrarDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="cerrar-periodo-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100">Confirmar Cierre de Periodo</DialogTitle>
          </DialogHeader>
          {pa && (
            <div className="space-y-4">
              <div className="bg-slate-800/40 border border-slate-700 rounded-sm p-3">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Periodo</p>
                <p className="text-base font-bold text-slate-200">{pa.etiqueta}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-800/40 p-3 rounded-sm">
                  <p className="text-[10px] uppercase text-slate-500">Ingresos</p>
                  <p className="text-sm font-bold text-emerald-400 font-mono">{formatoMXN(pa.totales.ingresos)}</p>
                </div>
                <div className="bg-slate-800/40 p-3 rounded-sm">
                  <p className="text-[10px] uppercase text-slate-500">Gastos</p>
                  <p className="text-sm font-bold text-red-400 font-mono">{formatoMXN(pa.totales.gastos)}</p>
                </div>
                <div className="bg-slate-800/40 p-3 rounded-sm">
                  <p className="text-[10px] uppercase text-slate-500">Utilidad</p>
                  <p className={`text-sm font-bold font-mono ${utilidadColor}`}>{formatoMXN(pa.totales.utilidad)}</p>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Notas (opcional)</label>
                <Textarea value={notas} onChange={e => setNotas(e.target.value)}
                  placeholder="Observaciones de este periodo..."
                  className="bg-slate-950 border-slate-700 text-slate-200" rows={3}
                  data-testid="notas-cierre-input" />
              </div>
              <div className="text-[11px] text-amber-500/80 bg-amber-500/10 border border-amber-500/30 p-2 rounded-sm">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Una vez cerrado, no se podran modificar los pagos ni gastos de este rango. Solo el propietario puede reabrirlo.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCerrarDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={cerrarPeriodo} disabled={guardando}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
              data-testid="confirmar-cierre-btn">
              {guardando ? 'Cerrando...' : 'Cerrar y Generar Reporte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Detalle Cierre */}
      <Dialog open={!!detalleDialog} onOpenChange={() => setDetalleDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          {detalleDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-barlow text-xl text-slate-100">{detalleDialog.etiqueta}</DialogTitle>
                <p className="text-xs text-slate-500">
                  {detalleDialog.frecuencia} · Cerrado por {detalleDialog.cerrado_por} el {detalleDialog.created_at?.slice(0,10)}
                </p>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-800/40 p-3 rounded-sm">
                    <p className="text-[10px] uppercase text-slate-500">Ingresos</p>
                    <p className="text-base font-bold text-emerald-400 font-mono">{formatoMXN(detalleDialog.totales.ingresos)}</p>
                  </div>
                  <div className="bg-slate-800/40 p-3 rounded-sm">
                    <p className="text-[10px] uppercase text-slate-500">Gastos</p>
                    <p className="text-base font-bold text-red-400 font-mono">{formatoMXN(detalleDialog.totales.gastos)}</p>
                  </div>
                  <div className="bg-slate-800/40 p-3 rounded-sm">
                    <p className="text-[10px] uppercase text-slate-500">Utilidad</p>
                    <p className="text-base font-bold text-cyan-400 font-mono">{formatoMXN(detalleDialog.totales.utilidad)}</p>
                  </div>
                </div>
                {detalleDialog.totales.top_servicios?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Top Servicios</p>
                    <div className="space-y-1">
                      {detalleDialog.totales.top_servicios.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-sm bg-slate-800/30 px-3 py-1.5 rounded-sm">
                          <span className="text-slate-300">{s.nombre} <span className="text-slate-500 text-xs">({s.count})</span></span>
                          <span className="text-cyan-400 font-mono text-xs">{formatoMXN(s.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detalleDialog.notas && (
                  <div className="bg-slate-800/40 p-3 rounded-sm">
                    <p className="text-[10px] uppercase text-slate-500 mb-1">Notas</p>
                    <p className="text-xs text-slate-300">{detalleDialog.notas}</p>
                  </div>
                )}
                <Button onClick={() => descargarPDF(detalleDialog)}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-bold">
                  <FileDown className="w-4 h-4 mr-2" /> Descargar PDF
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
